<template>
  <div class="storage-list__content">
    <Spinner
      v-if="loading"
      class="storage-list__spinner"
      size="md"
      color="dark"
      label="Storage table"
    />
    <DarwinTable
      :hover="!disabled"
      :columns="columnsWithSorting"
      :data="rowData"
      @sort="applySort"
    >
      <template #id="{ rowIndex }">
        <span class="storage__name-wrapper">
          <span class="storage__name">{{ rowData[rowIndex].bucket }}</span>
          <Badge
            v-if="rowData[rowIndex].default"
            class="storage__badge"
            label="Default"
            size="small"
            no-tooltip
            :color="{ r: 37, g: 99, b: 235, a: 1 }"
          />
        </span>
      </template>

      <template #provider="{ rowIndex }">
        {{ getInstanceType(rowData[rowIndex].provider) }}
      </template>

      <template #name="{ rowIndex }">
        {{ rowData[rowIndex].name }}
      </template>

      <template #region="{ rowIndex }">
        {{ rowData[rowIndex].region }}
      </template>

      <template #access="{ rowIndex }">
        <span v-if="rowData[rowIndex].readonly">read</span>
        <span v-else>read/write</span>
      </template>

      <template #prefix="{ rowIndex }">
        {{ rowData[rowIndex].prefix || '-' }}
      </template>

      <template #cloudfront_host="{ rowIndex }">
        {{ rowData[rowIndex].cloudfront_host && '✔️' }}
      </template>

      <template #actions="{ rowIndex }">
        <StorageActions
          :disabled="disabled"
          @details="onEdit(rowData[rowIndex])"
          @delete="maybeDeleteStorage(rowData[rowIndex])"
        />
      </template>
    </DarwinTable>

    <StorageModal
      v-model:open="open"
      :storage="editedStorage"
      @closed="editedStorage = null"
    />

    <ConfirmDialog
      v-model:open="deleteStorageDialogOpen"
      :disabled-confirm-action="loading"
      class-name="delete-storage-dialog"
      type="error"
      @confirm="deleteStorage"
    >
      <template #title> {{ deleteStorageDialogTitle }} </template>
      <template #detail>
        This is an irreversible action.
        <br />
        <br />
        Deleting your external storage integration will result in your existing files, thumbnails,
        and annotations being inaccessible even if the same storage bucket is re-added.
        <br />
        <br />
        Please contact support before proceeding if you are migrating your data from one bucket to
        another.
      </template>
      <template #confirm-text> REMOVE STORAGE </template>
    </ConfirmDialog>
  </div>
</template>

<script lang="ts">
export default {
  name: 'StorageList',
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'

import DarwinTable from '@/components/Common/DarwinTable.vue'
import { useAuth } from '@/modules/Auth/useAuth'
import { StorageProvider } from '@/store/types'
import type { StoragePayload } from '@/store/types/StoragePayload'
import { useToast } from '@/uiKit/Toast/useToast'
import StorageModal from './StorageModal.vue'
import StorageActions from './StorageActions.vue'
import Spinner from '@/uiKit/Spinner/Spinner.vue'
import Badge from '@/uiKit/Badge/Badge.vue'
import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import orderBy from 'lodash/orderBy'
import type { SortDirectionType } from '@/uiKit/types'
import { useExternalStorageStore } from '@/modules/ExternalStorage/useExternalStorageStore'
import { deleteStorage as requestDeleteStorage } from '@/backend/darwin/deleteStorage'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useBillingInfoLoader } from '@/modules/Billing/useBillingInfoLoader'

const toast = useToast()

const storageStore = useExternalStorageStore()

const rowData = computed(() => {
  const data = storageStore.storagesDefaultFirst.map((s) => ({ ...s, id: s.slug }))
  const field = Object.entries(sorting.value).find(
    (entry): entry is ['id' | 'name' | 'type', SortDirectionType] => entry[1] !== 'none',
  )
  return field ? orderBy(data, field[0], field[1]) : data
})

const open = ref(false)

const columns = [
  { key: 'id', label: 'Storage ID' },
  { key: 'provider', label: 'Type' },
  { key: 'name', label: 'Name' },
  { key: 'region', label: 'Region' },
  { key: 'access', label: 'Access' },
  { key: 'prefix', label: 'Prefix' },
  { key: 'cloudfront_host', label: 'CloudFront' },
  { key: 'actions' },
]

const editedStorage = ref<StoragePayload | null>(null)
const storageToDelete = ref<StoragePayload | null>(null)
const loading = ref(false)

const { isAuthorized } = useAuth()
const disabled = computed(() => !isAuthorized('update_team'))

const deleteStorageDialogOpen = ref(false)

const deleteStorageDialogTitle = computed(() =>
  storageToDelete.value
    ? `Please confirm you wish to remove "${storageToDelete.value.name}" storage.`
    : '',
)

const getInstanceType = (provider?: string): string => {
  if (!provider) {
    return ''
  }
  const options = [
    { label: 'AWS S3', id: StorageProvider.AWS },
    { label: 'Wasabi S3', id: StorageProvider.Wasabi },
    { label: 'GCP', id: StorageProvider.GCP },
    { label: 'Azure Blob Storage', id: StorageProvider.Azure },
    { label: 'MinIO', id: StorageProvider.Minio },
  ]
  return options.find((option) => option.id === provider)?.label || ''
}

const onEdit = (row: StoragePayload): void => {
  if (!disabled.value) {
    editedStorage.value = row
    open.value = true
  }
}

const maybeDeleteStorage = (storage: StoragePayload): void => {
  storageToDelete.value = storage
  deleteStorageDialogOpen.value = true
}

const teamStore = useTeamStore()
const billingInfoLoader = useBillingInfoLoader()

const deleteStorage = async (): Promise<void> => {
  if (!teamStore.currentTeam || !storageToDelete.value || loading.value) {
    return
  }

  const teamId = teamStore.currentTeam.id
  const teamSlug = teamStore.currentTeam.slug

  loading.value = true

  const result = await requestDeleteStorage({
    teamSlug,
    storageSlug: storageToDelete.value.slug,
  })

  if (result.ok) {
    storageStore.storages = storageStore.storages.filter(
      (s) => s.slug !== storageToDelete.value?.slug,
    )
  } else {
    toast.warning({ meta: { title: result.error.message as string } })
    return
  }

  await billingInfoLoader.load(teamId)
  deleteStorageDialogOpen.value = false
  loading.value = false

  storageToDelete.value = null
}

const sorting = ref<Record<'id' | 'provider' | 'name', SortDirectionType | 'none'>>({
  id: 'none',
  provider: 'none',
  name: 'none',
})

const isValidKey = (key: string): key is 'id' | 'provider' | 'name' => key in sorting.value

const applySort = (key: string, val: SortDirectionType | 'none'): void => {
  sorting.value = {
    id: key === 'id' ? val : 'none',
    provider: key === 'provider' ? val : 'none',
    name: key === 'name' ? val : 'none',
  }
}

const columnsWithSorting = computed(() =>
  columns.map((column) =>
    isValidKey(column.key) ? { ...column, sortBy: sorting.value[column.key] } : column,
  ),
)
</script>

<style lang="scss" scoped>
.storage-list__content {
  position: relative;
}
.storage-list__spinner {
  position: absolute;
  top: 50px;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
}
.storage__name-wrapper {
  display: inline-flex;
  align-items: center;

  .storage__name {
    word-break: break-all;
  }
  .storage__badge {
    margin-left: 4px;
  }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
/*  This `DarwinDialog` modal is normally called from `<modal>` and it needs to be on top of it.
    There is no current support to control z-index, so the below line is a workaround.
    `.delete-storage-dialog` is added to the dialog so we make sure the leaked css isn't
    changing the `z-index` of other dialogs.
*/
.darwin-dialog.delete-storage-dialog {
  z-index: var(--z-index-modal-level-2-overlay) !important;
}
</style>
